import {Detail, Icon, Link, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';

export interface BasedOnItem {
	variableName: string;
}

export interface BasedOnListProps {
	items: BasedOnItem[];
	maxVisible?: number;
}

const DEFAULT_MAX_VISIBLE = 5;

// Optional chaining for the same reason NovaInvocationControl needs it: this module loads
// transitively through the shared web-components barrel, and a test that mocks
// '@forecast-it/design-system' without providing tokensForThemeProvider would otherwise crash on
// import alone, before any test ran.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;
const size = tokensForThemeProvider?.sky?.sys?.size;

const ACCENT = sys?.text.content.primary ?? '';
const PANEL_SURFACE = sys?.background.primary['1'] ?? '';
const PANEL_BORDER = ref?.blue['100'] ?? '';
const CHIP_SURFACE = sys?.background.neutral['1'] ?? '';
const PANEL_RADIUS = size?.borderradius['3'] ?? '';
const CHIP_RADIUS = size?.borderradius['2'] ?? '';

// A bounded panel rather than a bare list: the sources are a footnote on the answer, and an edge
// around them is what stops them reading as more of what the agent said.
const Panel = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	padding: 8px 11px;
	background: ${PANEL_SURFACE};
	border: 1px solid ${PANEL_BORDER};
	border-radius: ${PANEL_RADIUS};
`;

const PanelLabel = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 5px;
	flex: none;
	color: ${ACCENT};

	svg path {
		fill: ${ACCENT};
	}

	/* Detail carries its own colour - its palette runs default/medium/disabled/white/error, none of
	   which is the accent this panel is drawn in - so it would otherwise paint the label in body
	   text over the accent set above. Inheriting keeps the typography component for its type scale
	   while this panel keeps the one decision it actually owns. */
	* {
		color: inherit;
	}
`;

const Chips = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px;
	flex: 1;
	min-width: 0;
`;

// Each source named and nothing else. The design system's Pill is the near miss here: its outlined
// variant carries a status dot, and there is no status to report - every name in this list is a
// source that was read, so a dot could only ever say the same thing five times. The chip is
// therefore drawn from tokens (surface, border, radius and text colour all design-system values)
// rather than by suppressing part of a component built for a different job.
const Chip = styled.div`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	height: 22px;
	padding: 0 8px;
	white-space: nowrap;
	background: ${CHIP_SURFACE};
	border: 1px solid ${PANEL_BORDER};
	border-radius: ${CHIP_RADIUS};
	color: ${ACCENT};

	/* Same reason as the panel label: the name inside is a Detail, which sets its own colour and
	   would otherwise render body text on a chip whose border and surface are drawn in the accent. */
	* {
		color: inherit;
	}
`;

export const BasedOnList = (props: BasedOnListProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [expanded, setExpanded] = useState(false);
	const maxVisible = props.maxVisible ?? DEFAULT_MAX_VISIBLE;

	if (props.items.length === 0) {
		return (
			<Text data-cy="nova-based-on-empty">{formatMessage({id: 'nova_invocation.based_on.no_variables_recorded'})}</Text>
		);
	}

	const visible = expanded ? props.items : props.items.slice(0, maxVisible);
	const hiddenCount = props.items.length - visible.length;

	return (
		<div data-cy="nova-based-on-list">
			<Panel>
				<PanelLabel>
					{/* aria-hidden: the label beside it already says what this panel is. */}
					<Icon icon="search" size="s" aria-hidden="true" />
					<Detail>{formatMessage({id: 'nova_invocation.based_on.label'})}</Detail>
				</PanelLabel>
				{/* role="list" holds only role="listitem" children — the label beside it and the "show
				    more" link below sit outside it, as this wrapper's own siblings, not inside it. */}
				<Chips role="list" aria-label={formatMessage({id: 'nova_invocation.based_on.label'})}>
					{visible.map((item, index) => (
						// Index-qualified: BasedOnItem doesn't guarantee variableName is unique, and a
						// duplicate would otherwise collide as a React key and mis-reconcile rows.
						<div role="listitem" key={`${index}-${item.variableName}`}>
							<Chip>
								<Detail>{item.variableName}</Detail>
							</Chip>
						</div>
					))}
				</Chips>
			</Panel>
			{hiddenCount > 0 && (
				// Link, not Button — this is an inline "reveal more of this list" affordance sitting
				// among plain text rows, not an action that warrants button chrome. Link supports
				// exactly this (no href, click-only) via its own documented default.
				<Link data-cy="nova-based-on-show-more" onClick={() => setExpanded(true)}>
					{formatMessage({id: 'nova_invocation.based_on.show_more'}, {count: hiddenCount})}
				</Link>
			)}
		</div>
	);
};
